import React from 'react';
import {
  Box,
  Container,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as lodash from 'lodash';

import {
  newFormStateOnAddressChange,
  prepareIdValueMap,
} from '@bizapp-lp/workflow/pages/StepUtils';
import {
  timeout,
  TIMEOUT_MSEC,
} from '@bizapp-lp/workflow/templates/MultiStepFormContainerGroup';
import { Header } from '@bizapp-lp/workflow/organisms/Header';
import { Footer } from '@bizapp-lp/workflow/organisms/Footer';
import { TwoColumnFormComponent } from '@bizapp-lp/workflow/organisms/TwoColumnFormComponent';
import {
  ProcessingDialog,
  ProcessingDialogState,
} from '@bizapp-lp/workflow/organisms/ProcessingDialog';
import {
  calcInitialState,
  FormDataChangeHandler,
  InputForm,
  InputValueChangeHandler,
} from '@bizapp-lp/workflow/organisms/InputForm';
import {
  InputComponentProps,
  InputProps,
  InputState,
} from '@bizapp-lp/workflow/organisms/InputFormType';
import { AddressType } from '@bizapp-lp/workflow/molecules/ZipcodeInput';
import { StyledButton } from '@bizapp-lp/workflow/molecules/StyledButton';
import { GrecaptchaTypography } from '@bizapp-lp/workflow/atoms/GrecaptchaTypography';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#F2f3fb',
      padding: theme.spacing(5, 1, 10),
    },
    header: {
      textAlign: 'center',
      '& .MuiTypography-h1': {
        fontSize: '24px',
        [theme.breakpoints.up('tablet')]: {
          fontSize: '32px',
        },
      },
      '& .MuiTypography-body1': {
        fontSize: '14px',
        margin: theme.spacing(2, 'auto', 4),
      },
    },
    form: {},
    submitButtonArea: {
      margin: theme.spacing(4, 0, 0),
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
    },
    agreements: {
      margin: theme.spacing(0, 'auto', 1.5),
      '& .MuiTypography-body1': {
        marginLeft: theme.spacing(1),
        fontSize: '14px',
      },
      '& .MuiLink-root': {
        color: '#1565C0',
      },
      '& .MuiCheckbox-root': {
        padding: theme.spacing(0),
        color: '#787F86',
        '& svg': {
          width: '21px',
          height: '21px',
        },
      },
    },
    submitButton: {
      marginRight: 'auto',
      marginLeft: 'auto',
      display: 'block',
      width: '100%',
      maxWidth: '400px',
      height: '48px',
    },
    backlink: {
      display: 'block',
      textAlign: 'center',
      margin: theme.spacing(2, 'auto', 0),
      color: '#1565C0',
      fontSize: '14px',
      cursor: 'pointer',
    },
  }),
);

const submitData = async (
  baseUrl: string,
  grToken: string,
  bodyObj: { [key: string]: string },
) => {
  const method = 'POST';
  const url = `${baseUrl}/api/registration/workflow-trial`;
  const headers = { 'Content-Type': 'application/json' };
  const _body = {
    serviceId: 'workflow-trial',
    formData: {
      step1: JSON.stringify(bodyObj),
    },
    grecaptchaToken: grToken,
  };

  return fetch(url, {
    method: method,
    headers: headers,
    body: JSON.stringify(_body),
    credentials: 'include',
  });
};

export interface TwoColumnFormProps {
  className?: string;
  apiBaseUrl: string;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  inputList: InputProps[];
  submitButtonLabel?: string;
  toUrl?: string;
}

const TwoColumnForm: React.FC<TwoColumnFormProps> = ({
  className = '',
  apiBaseUrl,
  heading,
  description,
  inputList,
  submitButtonLabel,
  toUrl = '',
}) => {
  const classes = useStyles();

  // current page form id
  const formIds = React.useMemo(
    () => inputList.filter((v) => v.kind !== 'sectionTitle').map((v) => v.id),
    [inputList],
  );

  // current page form data
  const [formState, setFormState] = React.useState<Map<string, InputState>>(
    calcInitialState(inputList),
  );

  // Input Form: validation callback result
  const [isFormStateValid, setIsFormStateValid] = React.useState(false);
  function onValidate(isFormValid: boolean) {
    setIsFormStateValid(isFormValid);
  }
  // Input Form: form update callback
  const handleFormStateChange: FormDataChangeHandler = (
    changedInputId,
    newFormState,
  ) => {
    setFormState(newFormState);
  };
  // Input Form: input blur callback
  const handleInputBlur: FormDataChangeHandler = (
    changedInputId,
    newFormState,
  ) => {
    setFormState(newFormState);
  };

  // Input Form: apply extra props for step1
  // replace default onChange/onBlur or applying input specified attributes
  function customizeComponentProps(
    inputProps: InputProps,
    props: InputComponentProps,
    onChange: InputValueChangeHandler,
    onBlur: InputValueChangeHandler,
  ) {
    const base = lodash.clone(props);
    switch (props.kind) {
      case 'zipcode':
        base.onSelectZipcode = (addr: AddressType) => {
          onChange(addr.zipcode);
          setFormState((prevState) =>
            newFormStateOnAddressChange(addr, inputProps, prevState),
          );
        };
        break;
      default: // no changes to other kind of inputs
    }
    return base;
  }

  // agreement checkbox
  const [agreementChecked, setAgreementChecked] = React.useState(false);
  const handleAgreementChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setAgreementChecked(ev.target.checked);
    },
    [],
  );

  // disable submit button when agreement checkbox is not checked or form is invalid
  const [enableSubmitButton, setEnableSubmitButton] = React.useState(false);
  React.useEffect(() => {
    setEnableSubmitButton(agreementChecked && isFormStateValid);
  }, [agreementChecked, isFormStateValid]);

  const [submitForm, setSubmitForm] = React.useState(false);
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
    // disable button to prevent multiple-submit
    if (!enableSubmitButton) {
      return;
    }
    setEnableSubmitButton(false);
    setSubmitForm(true);
  };

  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // show waiting overlay or error dialog
  const [submitState, setSubmitState] = React.useState<ProcessingDialogState>(
    'close',
  );
  const handleDialogClose = () => {
    setSubmitState('close');
  };

  const submit = React.useCallback(
    async (formState: Map<string, InputState>) => {
      if (executeRecaptcha === undefined) {
        return Promise.reject(new Error('Site key is undefined'));
      }
      let token: string = '';
      try {
        token = await executeRecaptcha('trial');
      } catch (error) {
        return Promise.reject(
          new Error(
            `Probably, this domain is not supported for this site key. ${error}`,
          ),
        );
      }

      const data = prepareIdValueMap(formState, formIds);
      try {
        const resp = await submitData(apiBaseUrl, token, data);
        if (resp.status === 201) {
          return Promise.resolve();
        }
      } catch (error) {
        return Promise.reject(new Error(error));
      }
      return Promise.resolve();
    },
    [executeRecaptcha, apiBaseUrl, formIds],
  );

  React.useEffect(() => {
    if (!submitForm) {
      return;
    }
    setSubmitState('wait');
    setSubmitForm(false);

    (async () => {
      try {
        await timeout(submit(formState), TIMEOUT_MSEC);
        navigate(toUrl, { state: { valid: true } });
      } catch {
        setSubmitState('error');
        setEnableSubmitButton(true);
      }
    })();
  }, [submitForm, submit, formState, navigate, toUrl]);

  return (
    <>
      <Header from="workflow" />
      <Container
        disableGutters
        maxWidth={false}
        className={`${classes.root} ${className}`}
      >
        <Container
          component="header"
          disableGutters
          maxWidth={false}
          className={classes.header}
        >
          <Typography variant="h1">{heading}</Typography>
          <Typography>{description}</Typography>
        </Container>

        <InputForm
          inputConfigs={inputList}
          formState={formState}
          onChange={handleFormStateChange}
          onBlur={handleInputBlur}
          onValidate={onValidate}
          customizeComponentProps={customizeComponentProps}
        >
          {(inputComponentPropsList) => (
            <TwoColumnFormComponent
              inputList={inputComponentPropsList}
              className={`${classes.form} ${className}`}
            >
              <Box className={classes.submitButtonArea}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreementChecked}
                      onChange={handleAgreementChange}
                    />
                  }
                  label={
                    <Typography>
                      <Link
                        href="/terms/workflow"
                        target={'_blank'}
                        color="initial"
                      >
                        利用規約
                      </Link>
                      および
                      <Link
                        href="/privacy/workflow"
                        target={'_blank'}
                        color="initial"
                      >
                        個人情報の取り扱い
                      </Link>
                      について同意する。
                    </Typography>
                  }
                  className={classes.agreements}
                />
                <StyledButton
                  variant="primary"
                  label={submitButtonLabel}
                  disabled={!enableSubmitButton}
                  onClick={handleClick}
                  className={classes.submitButton}
                />
                <GrecaptchaTypography />
                <Link
                  onClick={() => {
                    navigate(-1);
                  }}
                  className={classes.backlink}
                >
                  {'前のページへ戻る'}
                </Link>
              </Box>
            </TwoColumnFormComponent>
          )}
        </InputForm>
        <ProcessingDialog
          state={submitState}
          onClickError={handleDialogClose}
        />
      </Container>
      <Footer />
    </>
  );
};

export default TwoColumnForm;
